import { useState } from 'react'
import { Text, Checkbox } from '@vibe/core'
import './RecipeConfigForm.css'

export default function RecipeConfigForm({ recipeType, templates, boardColumns, integration, onSubmit, onCancel }) {
  const [templateId, setTemplateId] = useState(integration?.template_id || '')
  const [triggerColumn, setTriggerColumn] = useState(integration?.trigger_column || '')
  const [triggerValue, setTriggerValue] = useState(integration?.trigger_value || '')
  const [recipientColumns, setRecipientColumns] = useState(() => {
    if (!integration?.recipient_columns) return []

    try {
      const parsed = JSON.parse(integration.recipient_columns)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  })
  const [ccEnabled, setCcEnabled] = useState(integration?.cc_enabled === 1)

  const triggerConfigMap = {
    status_change: {
      label: 'Status Column',
      help: 'Select the status column that drives this automation.',
      columnTypes: ['status'],
      valueLabel: 'Trigger When Status Is',
      valueHelp: 'Choose the status value that should send the email.'
    },
    date_reached: {
      label: 'Date Column',
      help: 'Select the date column to watch. The email sends when that item date reaches today.',
      columnTypes: ['date']
    },
    person_assigned: {
      label: 'Person Column',
      help: 'Select the person column that acts as the trigger.',
      columnTypes: ['people']
    }
  }

  const triggerConfig = triggerConfigMap[recipeType]
  const triggerColumns = triggerConfig
    ? (boardColumns?.filter(c => triggerConfig.columnTypes.includes(c.type)) || [])
    : []
  const recipientOptions = boardColumns?.filter(c => ['email', 'people', 'text', 'mirror'].includes(c.type)) || []
  const selectedTriggerColumn = boardColumns?.find(c => c.id === triggerColumn)
  const triggerLabels = Array.isArray(selectedTriggerColumn?.settings_labels) ? selectedTriggerColumn.settings_labels : []

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit({
      template_id: templateId,
      trigger_column: triggerConfig ? triggerColumn : '',
      trigger_value: triggerConfig?.valueLabel ? triggerValue : '',
      recipient_columns: recipientColumns,
      cc_enabled: ccEnabled
    })
  }

  const needsTriggerColumn = Boolean(triggerConfig)
  const needsTriggerValue = Boolean(triggerConfig?.valueLabel)

  return (
    <form id="recipe-config-form" className="recipe-config-form" onSubmit={handleSubmit}>
      <div className="config-intro">
        <Text weight="bold" className="config-title">Integration config</Text>
        <Text className="config-subtitle">Choose the template, trigger condition, and recipient source according to each field's role.</Text>
      </div>

      {/* Template Selection */}
      <div className="form-section form-section-template" style={{ animationDelay: '0.05s' }}>
        <div className="field-label">
          <div className="field-mark field-mark-template" />
          <div className="field-copy">
            <Text weight="bold">Email Template</Text>
            <Text className="field-help">The template controls the email body, preview text, and attachments.</Text>
          </div>
        </div>
        <div className="field-control field-control-template">
          <select
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            className="custom-select"
          >
            <option value="">Select a template to send...</option>
            {templates?.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Trigger Column Selection */}
      {needsTriggerColumn && (
        <div className="form-section form-section-trigger" style={{ animationDelay: '0.1s' }}>
          <div className="field-label">
            <div className="field-mark field-mark-trigger" />
          <div className="field-copy">
            <Text weight="bold">{triggerConfig.label}</Text>
            <Text className="field-help">{triggerConfig.help}</Text>
          </div>
          </div>
          <div className="field-control field-control-trigger">
            <select
              value={triggerColumn}
              onChange={(e) => setTriggerColumn(e.target.value)}
              className="custom-select"
            >
              <option value="">{`Select a ${triggerConfig.label.toLowerCase()}...`}</option>
              {triggerColumns.map(col => (
                <option key={col.id} value={col.id}>{col.title}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Trigger Value (for status_change) */}
      {needsTriggerValue && triggerColumn && (
        <div className="form-section form-section-trigger-value" style={{ animationDelay: '0.15s' }}>
          <div className="field-label">
            <div className="field-mark field-mark-value" />
          <div className="field-copy">
            <Text weight="bold">{triggerConfig.valueLabel}</Text>
            <Text className="field-help">{triggerConfig.valueHelp}</Text>
          </div>
          </div>
          <div className="field-control field-control-value">
            <select
              value={triggerValue}
              onChange={(e) => setTriggerValue(e.target.value)}
              className="custom-select"
            >
              <option value="">Select the target status...</option>
              {triggerLabels.map(label => (
                <option key={label} value={label}>{label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Recipient Columns Selection */}
      <div className="form-section form-section-recipients" style={{ animationDelay: '0.2s' }}>
        <div className="field-label">
          <div className="field-mark field-mark-recipient" />
          <div className="field-copy">
            <Text weight="bold">Recipient Columns</Text>
            <Text className="field-help">Choose columns that contain email addresses or data that can be resolved into emails.</Text>
          </div>
        </div>
        <div className="recipient-grid" role="group" aria-label="Recipient columns">
          {recipientOptions.map(col => {
            const isSelected = recipientColumns?.includes(col.id);
            return (
              <button
                key={col.id}
                type="button"
                className={`recipient-item ${isSelected ? 'selected' : ''}`}
                onClick={() => {
                  if (isSelected) {
                    setRecipientColumns(recipientColumns.filter(id => id !== col.id))
                  } else {
                    setRecipientColumns([...recipientColumns, col.id])
                  }
                }}
                aria-pressed={isSelected}
              >
                <div className="recipient-item-main">
                  <Checkbox checked={isSelected} readOnly />
                  <div className="recipient-meta">
                    <span className="recipient-label">{col.title}</span>
                    <span className={`recipient-type recipient-type-${col.type}`}>{col.type}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* CC Enabled */}
      <div className="form-section form-section-cc" style={{ animationDelay: '0.25s' }}>
        <div className="cc-toggle">
          <Checkbox 
            checked={ccEnabled} 
            onChange={(e) => setCcEnabled(e.target.checked)} 
          />
          <div className="cc-text-container">
            <span className="cc-title">CC all recipients</span>
            <span className="cc-desc">All selected recipients will see each other in the email.</span>
          </div>
        </div>
      </div>
    </form>
  )
}
